import { atom } from "jotai";
import { IOrderDTO } from "../order";
import { IUser } from "../login";
import { IPrinter } from "../printers";
import { IOrderPullFlag } from "../pull_flags/types";


export const currentWeightScanItemAtom = atom<IWeightScanResponse | null>(null);
export const weightScanItemLoadingAtom = atom<boolean>(false);
export const weighQueueAtom = atom<IWeighQueue[]>([]);
export const currentScanItemAtom = atom<IScanResponse | null>(null);
export const scanLoadingAtom = atom<boolean>(false);
export const lastScanValueAtom = atom<string>('');
export const currentShippingScanItemAtom = atom<IShippingScanResponse | null>(null);
export const shippingScanLoading = atom<boolean>(false);

export const manualSearchLoadingAtom = atom<boolean>(false);

export const insertingWasteAtom = atom<boolean>(false);

export const wasteTrackingCategoriesAtom = atom<IWasteCategory[]>([]);

export const currentWasteTrackingAtom = atom<IWasteTracking[]>([]);

export const sortedWasteDataAtom = atom<ISortedWasteData[]>([{
    categoryTitle: '',
    data: [{
        categorySubtitle: '',
        weight_pounds: 0,
        weight_ounces: 0
    }]
}]);

export const orderShippingInfoAtom = atom<IShippingScanResponse | null>(null);

export const wasteTrackingFiltersAtom = atom<IWasteTrackingFilters>({
    from: '',
    to: '',
    sortBy: 'material'
});

export interface IScanResponse {
    order: IOrderDTO;
    zpl: string;
    lineItems: IOrderLineItemDTO[];
    orderConsumables: IOrderConsumables;
    scanValue: string;
    /** Active pull ("do not ship") flag for this order, acknowledged or not. Null/absent when none. */
    pullFlag?: IOrderPullFlag | null;
}

export interface IOrderConsumables {
    id: number;
    created_at: string;
    order_id: number;
    small_box: number;
    large_box: number;
    paste: number;
    squeegee: number;
}

export interface IOrderLineItemDTO {
    id: number;
    print_range: string;
    line_item_jdml: string;
    dc_design?: {
        id: number;
        title: string;
        shopify_product_id: string;
        pattern_type: string;
        collection: {
            id: number;
            name: string;
        };
        current_version: {
            id: number;
            dpi: number;
            width_px: number;
            height_px: number;
            width_in: number;
            height_in: number;
            image_url: string;
            preview_url?: string | null;
        };
        release: {
            id: number;
            design_peel_rpe: string;
            design_luxe_rpe: string;
            design_wrap_rpe: string;
            sample_peel_rpe: string;
            sample_luxe_rpe: string;
            rolltoroll_peel_rpe: string;
            rolltoroll_luxe_rpe: string;
        };
    };
    consumable: {
        id: number;
        name: string;
        image: string;
        sku: string;
    };
    giftwrap: {
        design_name: string;
        thumbnail: string;
    }
    // Patternly line items: no dc_design/design/consumable, but a material + 5-segment sku.
    // Set by order-handler (order_line_item_repository) from the pipe-encoded sku.
    patternly?: {
        design_name: string;
        thumbnail: string;
    }
    material: {
        id: number;
        category: string;
    } | null;
    order_id: number;
    height: string;
    width: number;
    droppable: boolean;
    jdml_dropped: boolean;
    drops_validated: boolean;
    line_item_scans: IOrderLineItemScanDTO[];
    print_id: string;
    line_item_issues: IOrderLineItemIssueDTO[];
    line_item_reprints: IOrderLineItemReprintsDTO[];
    name?: string;
    sku: string;
    version?: number;
    print_count?: number;
    linked_to?: number | null;
}

export interface IOrderLineItemReprintsDTO {
    id: number;
    scan_time: string;
    scanned: boolean;
    bin: string;
    reprint_jdml: string;
    created_at: string;
    range_start: number;
    range_end: number;
    print_id: string;
}

export interface IOrderLineItemIssueDTO {
    id: number;
    employee: {
        id: number;
        first: string;
        last: string;
    };
    issue: {
        id: number;
        value: string;
    };
    created_at: string;
}

export interface IOrderLineItemScanDTO {
    id: number;
    scan_time: string;
    scanned: boolean;
    split_index: number;
    bin: string;
    print_range_start: number;
    print_range_end: number;
}

export interface IScanRequest {
    scanValue: string;
    isUnscan: boolean;
    loadedOrder: number | null;
    printerId?: IPrinter;
    channel?: number;
}

export interface IManualSearch {
    query: string;
}

export interface IWeightScanResponse {
    [key: string]: any;
}

export type QueueStatus = 'unprocessed' | 'processing' | 'processed' | 'failed';

export interface IWeighQueue {
    id?: number;
    created_at: string;
    box_weights: IBoxWeight;
    waste_weights: IWasteWeight;
    process_status: QueueStatus;
    channel?: number;
}

export interface IBoxWeight {
    id?: number;
    created_at: string;
    box_type: 'large' | 'medium' | 'small';
    weight_pounds: number;
    weight_ounces: number;
    order: {
        id: number;
        line_items: {
            id: number;
            scan: {
                id: number;
                scanned: boolean;
            }[]
        }[]
    }
    tag_text: string;
}

export interface IWasteWeight {
    id?: number;
    created_at: string;
    weight_pounds: number;
    weight_ounces: number;
    barcode: string;
    waste_category: IWasteCategory;
}

export interface IShippingBoxWeight {
    id?: number;
    created_at: string;
    box_type: 'large' | 'medium' | 'small';
    weight_pounds: number;
    weight_ounces: number;
    order_id: number;
    tag_text: string;
    scanned: boolean;
    scan_time: string;
    employee_id: IUser;
    box_group: number;
}

export interface IShippingScanResponse {
    boxWeights: IShippingBoxWeight[];
    order: IOrderDTO;
    shippingRates: IRates[];
    /** ACTIVE shipments only — voided labels are not included here. */
    shipments: IShipment[];
    /**
     * Labels that were voided on this order. Shown so operators can see that a
     * label existed and what happened to it, rather than an empty panel.
     */
    voidedShipments?: IShipment[];
    /**
     * Set when a label on this order was bought with a ship date other than the one
     * the station-wide setting resolves to now — typically a label pre-bought at
     * confirm-complete before the date was changed.
     *
     * ADVISORY ONLY. A bought label is immutable and voiding it costs money, so the
     * station warns and still prints.
     *
     * The warning shown on a label is derived from the live setting instead of this
     * snapshot, which is fixed at scan time and goes stale as soon as the date changes.
     */
    shipDateWarning?: IShipDateWarning;
}

export interface IShipDateWarning {
    /** The date printed on the existing label, `YYYY-MM-DD`. */
    labelShipDate: string;
    /** The date the setting resolves to right now, `YYYY-MM-DD`. */
    currentShipDate: string;
}

export interface IShippingScanRequest {
    scanValue: string;
    employee: number;
    printer: IPrinter;
}

export interface IRates {
    box_group: number;
    group_weight: number;
    rates: IRate[];
    selectedRate: IShipmentCode;
    boxCount: 1 | 2;
}

export interface IRate {
    serviceName: string;
    serviceCode: string;
    shipmentCost: number;
    otherCost: number;
    shipmentCodes: IShipmentCode;
}

export interface IShipmentCode {
    carrierCode: CarrierCode;
    packageCode: PackageCode;
    serviceCode: ServiceCode;
    dimensions: DimensionGroup;
}

export type StampsPackageCode =
    'package' |
    'flat_rate_envelope' |
    'flat_rate_legal_envelope' |
    'flat_rate_padded_envelope' |
    'large_envelope_or_flat' | // SAMPLES ENVELOPE
    'large_flat_rate_box' |
    'large_package' |
    'letter' |
    'medium_flat_rate_box' | // MEDIUM BOXES FOR PO BOXES
    'non_rectangular' |
    'regional_rate_box_a' |
    'regional_rate_box_b' |
    'small_flat_rate_box' | // SMALL BOXES FOR PO BOXES
    'thick_envelope';

export type StampsServiceCode =
    'usps_priority_mail' |
    'usps_priority_mail_express' |
    'usps_parcel_select' |
    'usps_first_class_mail' |
    'usps_media_mail' |
    'usps_ground_advantage' | // DOMESTIC SAMPLES AND ALL PO BOXES
    'usps_priority_mail_international' |
    'usps_priority_mail_express_international' |
    'usps_first_class_mail_international' |
    'globalpost_economy' |
    'globalpost_priority' |
    'gp_plus' |
    'globalpost_parcel_select_smart_saver';

export type FedexPackageCode =
    'package' |
    'fedex_envelope_onerate' |
    'fedex_extra_large_box_onerate' |
    'fedex_large_box_onerate' |
    'fedex_medium_box_onerate' | // THIS IS OUR FEDEX FLAT RATE PACKAGE CODE
    'fedex_pak_onerate' |
    'fedex_small_box_onerate' | // This is our fedex flat rate for the small boxes
    'fedex_tube_onerate' |
    'fedex_10kg_box' |
    'fedex_25kg_box' |
    'fedex_box' |
    'fedex_envelope' | // INTERNATIONAL SAMPLES
    'fedex_extra_large_box' |
    'fedex_large_box' |
    'fedex_medium_box' | // INTERNATIONAL BOXES
    'fedex_pak' |
    'fedex_small_box' | // INTERNATIONAL SMALL BOXES
    'fedex_tube';

export type FedexServiceCode =
    'fedex_international_priority_express' |
    'fedex_ground' |
    'fedex_home_delivery' |
    'fedex_2day' | // DOMESTIC BOXES ALL SIZES
    'fedex_2day_am' |
    'fedex_express_saver' |
    'fedex_standard_overnight' |
    'fedex_priority_overnight' |
    'fedex_first_overnight' |
    'fedex_1_day_freight' |
    'fedex_2_day_freight' |
    'fedex_3_day_freight' |
    'fedex_first_overnight_freight' |
    'fedex_ground_international' |
    'fedex_international_economy' |
    'fedex_international_priority' |
    'fedex_international_first' |
    'fedex_international_economy_freight' |
    'fedex_international_priority_freight' |
    'fedex_international_connect_plus'; // ALL INTERNATIONAL SHIPMENTS

export type DHLPackageCode =
    'package' |
    'dhl_express_wallet_exp_env';

export type DHLServiceCode =
    'dhl_express_worldwide';

export type UPSPackageCode =
    'package' |
    'ups__express_box_large' |
    'ups_10_kg_box' |
    'ups_25_kg_box' |
    'ups_express_box' |
    'ups_express_box_medium' | // NEIGHBORING STATES
    'ups_express_box_small' | // NEIGHBORING STATES
    'ups_express_pak' |
    'ups_letter' |
    'ups_tube';

export type UPSServiceCode =
    'ups_ground_saver' |
    'ups_ground' | // NEIGHBORING STATES
    'ups_ground_international' |
    'ups_standard_international' |
    'ups_3_day_select' |
    'ups_worldwide_saver' |
    'ups_worldwide_express' |
    'ups_2nd_day_air' |
    'ups_worldwide_expedited' |
    'ups_worldwide_express_plus' |
    'ups_2nd_day_air_am' |
    'ups_next_day_air_saver' |
    'ups_next_day_air' |
    'ups_next_day_air_early_am';

export type PackageCode = StampsPackageCode | FedexPackageCode | DHLPackageCode | UPSPackageCode;
export type ServiceCode = StampsServiceCode | FedexServiceCode | DHLServiceCode | UPSServiceCode;
export type CarrierCode = 'stamps_com' | 'fedex' | 'dhl_express_worldwide' | 'ups_walleted';

export enum DimensionGroup {
    LARGE_BOX,
    SMALL_BOX,
    ENVELOPE
}

/**
 * Why a shipment was voided. Mirrors ShipmentVoidReason in order-handler and the
 * order_shipments_void_reason_chk constraint in the database.
 */
export type ShipmentVoidReason =
    | 'RECONFIRM_SUPERSEDED'
    | 'MANUAL_VOID'
    | 'CARRIER_REJECTED'
    | 'DUPLICATE'
    | 'DATA_REPAIR'
    | 'OTHER';

export interface IShipment {
    id?: number;
    created_at: string;
    label_data: string;
    shipment_id: number;
    box_group: IRates;
    order_id: number;
    shipment_type: ShipmentType;
    shipment_codes: IShipmentCode;
    printed: boolean;
    shipment_cost: number;
    tracking_number: string;
    tracking_url: string;
    /**
     * The ship date this label was actually bought with, `YYYY-MM-DD`.
     *
     * Null on every row written before the ship date setting existed — a null means
     * "unknown", never "stale", so it must not raise a warning.
     */
    ship_date?: string | null;

    // ---- Void state. Shipment rows are never deleted, only voided. ----
    voided?: boolean;
    voided_at?: string | null;
    voided_by?: number | null;
    void_reason?: ShipmentVoidReason | null;
    void_detail?: string | null;
    /**
     * Whether the carrier accepted the void. `false` means the label may still be
     * live: if the box already entered the carrier network it will still deliver,
     * so the tracking number stays meaningful and must still be shown.
     */
    carrier_void_succeeded?: boolean | null;
    superseded_by?: number | null;
}

/** Human-readable label for a void reason. */
export const VOID_REASON_LABELS: Record<ShipmentVoidReason, string> = {
    RECONFIRM_SUPERSEDED: 'Replaced by re-confirm',
    MANUAL_VOID: 'Voided by operator',
    CARRIER_REJECTED: 'Rejected by carrier',
    DUPLICATE: 'Duplicate label',
    DATA_REPAIR: 'Data repair',
    OTHER: 'Voided',
};

export enum ShipmentType {
    NEIGHBORING_MEDIUM_BOXES = 'NEIGHBORING_MEDIUM_BOXES',
    NEIGHBORING_SMALL_BOXES = 'NEIGHBORING_SMALL_BOXES',
    NEIGHBORING_SAMPLES = 'NEIGHBORING_SAMPLES',
    DOMESTIC_MEDIUM_BOXES = 'DOMESTIC_MEDIUM_BOXES',
    DOMESTIC_SMALL_BOXES = 'DOMESTIC_SMALL_BOXES',
    DOMESTIC_SAMPLES = 'DOMESTIC_SAMPLES',
    INTERNATIONAL_MEDIUM_BOXES = 'INTERNATIONAL_MEDIUM_BOXES',
    INTERNATIONAL_SMALL_BOXES = 'INTERNATIONAL_SMALL_BOXES',
    INTERNATIONAL_SAMPLES = 'INTERNATIONAL_SAMPLES',
    PO_BOX_SMALL = 'PO_BOX_SMALL',
    PO_BOX_LARGE = 'PO_BOX_LARGE',
    PO_SAMPLES = 'PO_SAMPLES'
}

export interface IWasteTracking {
    id?: number;
    material_id: number;
    waste_category_id: number;
    weight_pounds: number;
    weight_ounces: number;
    barcode: string;
    created_at?: string;
}

export interface IWasteTrackingRequestBody {
    id: number;
    weight_pounds: number;
    weight_ounces: number;
    queue_id?: number;
}

export interface IWasteCategory {
    id: number;
    category: string;
    name: string;
    description: string;
}

export interface ISearchWasteTrackingData {
    to: string,
    from: string,
}

export interface ISortedWasteData {
    categoryTitle: string;
    data: ISortedWasteSubData[]
}

export interface ISortedWasteSubData {
    categorySubtitle: string;
    weight_pounds: number;
    weight_ounces: number;
}

export interface IWasteTrackingFilters {
    from: string;
    to: string;
    sortBy: "material" | "category";
}

/**
 * Resolves display-friendly design data from dc_design (preferred) or a Patternly item.
 * Use for UI display: title, thumbnail, collection name, shopify ID.
 *
 * NOTE: Do NOT use the returned `id` for scanner barcode construction — Patternly items
 * have no design record (id is 0) and are scanned by print_id like any line item.
 */
export const resolveDesignDisplay = (lineItem: IOrderLineItemDTO, brandName?: string) => {
    if (lineItem.dc_design) {
        return {
            id: lineItem.dc_design.id,
            name: lineItem.dc_design.title,
            shopify_id: lineItem.dc_design.shopify_product_id,
            collection_name: lineItem.dc_design.collection?.name ?? '',
            thumbnail: lineItem.dc_design.current_version?.preview_url || lineItem.dc_design.current_version?.image_url || '',
        };
    }
    // Patternly items have no dc_design record; their display data is packed onto `patternly`
    // by order-handler (design_name + thumbnail from the pipe-encoded sku). Show the order's
    // real brand name when available, falling back to 'Patternly' for legacy/unknown brands.
    if (lineItem.patternly) {
        return {
            id: 0,
            name: lineItem.patternly.design_name,
            shopify_id: '',
            collection_name: brandName || 'Patternly',
            thumbnail: lineItem.patternly.thumbnail,
        };
    }
    // Legacy design fallback removed — all designs now use dc_design
    return null;
};

/**
 * Identifies overstock items: no design, no dc_design, no consumable, no material, not Patternly.
 * True overstock has material=null. Patternly items (material present + `patternly` set) and
 * giftwrap (material_id=3) are NOT overstock.
 */
export const isOverstockItem = (item: IOrderLineItemDTO): boolean => {
    return !item.dc_design && !item.consumable && !item.material && !item.patternly;
};
